// Shared with the toolroom operator app (ToolRoomOperator/src/progress.ts) - keep the two in step.
// Where a running job should be, worked out from when it started and the run time of each cutting tool on its
// program. It's an estimate: the API records when a job started but not how long it was paused, so time spent
// paused counts as running.

export interface CutterRun {
	runTime?: number | null;
	toolNumber?: string;
}

export interface JobProgress {
	// Index into the cutters of the one that should be running, or -1 when the program has no run times.
	currentIndex: number;
	currentRemainingMinutes: number;
	elapsedMinutes: number;
	finishAt: Date;
	overrun: boolean;
	percent: number;
	remainingMinutes: number;
	// Each cutter's share of the total, for a segmented bar.
	segments: { index: number; startPercent: number; widthPercent: number }[];
	totalMinutes: number;
}

export function ToolroomJobProgress(dateStarted: string | undefined, cutters: CutterRun[] | undefined, estimatedRunHours: number | undefined, now: Date = new Date()): JobProgress | null {
	if (!dateStarted) return null;
	const started = new Date(dateStarted).getTime();
	if (Number.isNaN(started)) return null;

	const runTimes = (cutters || []).map((c) => Math.max(0, Number(c.runTime) || 0));
	const cutterTotal = runTimes.reduce((a, b) => a + b, 0);
	// The cutters' run times are the plan when the programmer has filled them in; otherwise the operation's estimate.
	const totalMinutes = cutterTotal > 0 ? cutterTotal : (estimatedRunHours || 0) * 60;
	if (totalMinutes <= 0) return null;

	const elapsedMinutes = Math.max(0, (now.getTime() - started) / 60000);
	const remainingMinutes = Math.max(0, totalMinutes - elapsedMinutes);

	let currentIndex = -1;
	let currentRemainingMinutes = 0;
	const segments: JobProgress["segments"] = [];
	if (cutterTotal > 0) {
		let cumulative = 0;
		runTimes.forEach((minutes, index) => {
			segments.push({ index, startPercent: (cumulative / cutterTotal) * 100, widthPercent: (minutes / cutterTotal) * 100 });
			if (currentIndex === -1 && minutes > 0 && elapsedMinutes < cumulative + minutes) {
				currentIndex = index;
				currentRemainingMinutes = cumulative + minutes - elapsedMinutes;
			}
			cumulative += minutes;
		});
		// Past the planned time: the last cutter with a run time is still the best guess.
		if (currentIndex === -1) {
			for (let i = runTimes.length - 1; i >= 0; i--) {
				if (runTimes[i] > 0) { currentIndex = i; break; }
			}
		}
	}

	return {
		currentIndex,
		currentRemainingMinutes,
		elapsedMinutes,
		finishAt: new Date(started + totalMinutes * 60000),
		overrun: elapsedMinutes > totalMinutes,
		percent: Math.min(100, (elapsedMinutes / totalMinutes) * 100),
		remainingMinutes,
		segments,
		totalMinutes,
	};
}
